import React, { useState, useEffect, useRef, useCallback } from "react";
import { collection, getDocs } from "firebase/firestore";
import { createLinkedConfiguration, newLinkedCreationAttempt } from './createLinkedConfiguration';
import { db } from "../utils/firebase";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import ListSubheader from "@mui/material/ListSubheader";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Save, Trash2, X, History, RotateCcw, Play, Copy, FileText, ChevronDown, ChevronRight, Code, Eye, Table as TableIcon, Download, HelpCircle, Upload } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../utils/authContext";
import ModalPortal from "../utils/ModalPortal";
import SafeHtml from "../components/common/SafeHtml";
import {
  DEFAULT_FORM_DATA,
  fetchPrompt,
  fetchPrePrompts,
  fetchPatients,
  fetchPatientWithHistory,
  fetchCurrentContextTestDefaults,
  formatCurrentContextDateTime,
  fetchReferenceDataTypes,
  fetchReferenceDataItems,
  fetchDocumentFieldTypes,
  savePrompt,
  publishPrompt,
  deletePrompt,
  testPrompt,
  parseVersionToFormData,
  assemblePrompt,
  parseHistoryFields,
  toggleHistoryField,
  loadSampleTranscript,
  validateFormData,
  validateTestRequirements,
  hasFormDataChanged,
  calculateTestStats,
  fetchRagExamples,
  exportRagExamplesToJsonl
} from "./AdminPromptUtils";
import { useHistoryFieldOptions } from "./useHistoryFieldOptions";
import { sortByDisplayLabel } from "./sortByDisplayLabel";
import SearchableAdminSelect from "./components/SearchableAdminSelect";
import AdminNoteEditor from "./components/AdminNoteEditor";
import { useGeminiModels, canSelectGeminiModel } from "./useGeminiModels";
import ReadOnlyTextRenderer from "./components/ReadOnlyTextRenderer";
import GeminiModelLabel from "./components/GeminiModelLabel";
import { ADMIN_TABS, adminTabPath, dataTabPath } from './adminNavigation';
import Link from '@mui/material/Link';

const AdminPromptBuilder = () => {
  const navigate = useNavigate();
  const { promptId } = useParams();
  const isEditing = Boolean(promptId);
  const { currentUser } = useAuth();
  const { models, defaultModel, modelError, modelPolicyEnforced } = useGeminiModels();

  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null });
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [versionHistory, setVersionHistory] = useState([]);
  const [prePromptOptions, setPrePromptOptions] = useState([]);
  const [patients, setPatients] = useState([]);
  const [isPatientsLoading, setIsPatientsLoading] = useState(true);
  const [referenceDataTypes, setReferenceDataTypes] = useState([]);
  const [documentFieldTypes, setDocumentFieldTypes] = useState([]);

  const [selectedPatient, setSelectedPatient] = useState('');
  const [transcript, setTranscript] = useState('');
  const [testResult, setTestResult] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);
  const [isFieldSelectorCollapsed, setIsFieldSelectorCollapsed] = useState(false);
  const [testStats, setTestStats] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [outputView, setOutputView] = useState('raw'); // 'raw', 'rendered', 'table', or 'prompt'
  const [assembledPrompt, setAssembledPrompt] = useState(''); // Final prompt with all injections
  const [currentContextDocumentTypes, setCurrentContextDocumentTypes] = useState([]);
  const [currentContextUploadMode, setCurrentContextUploadMode] = useState(false);
  const [currentContextTest, setCurrentContextTest] = useState({
    providerName: '',
    providerCredentials: '',
    documentName: '',
    sessionDuration: '00:53:00',
    currentDateTime: new Date().toISOString(),
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  });

  // Dynamic history field options from Firestore
  const {
    options: historyFieldOptions, hiddenCount: hiddenHistoryFieldCount,
    loading: historyFieldsLoading, error: historyFieldsError, retry: retryHistoryFields,
  } = useHistoryFieldOptions();

  // RAG Examples state
  const [ragExamples, setRagExamples] = useState([]);
  const [isLoadingRagExamples, setIsLoadingRagExamples] = useState(false);
  // Track initial form data for unsaved changes detection
  const initialFormDataRef = useRef(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Track if we just created a prompt (to skip redundant load after navigate)
  const justCreatedRef = useRef(false);
  
  // Create Step/Section modal after first save
  const [createStepSectionModalOpen, setCreateStepSectionModalOpen] = useState(false);
  const [createStep, setCreateStep] = useState(true);
  const [createSection, setCreateSection] = useState(true);
  const [createdPromptData, setCreatedPromptData] = useState(null);
  const [isCreatingStepSection, setIsCreatingStepSection] = useState(false);
  const [linkedCreationError, setLinkedCreationError] = useState('');
  const linkedCreationAttempt = useRef(null);
  const linkedCreationBusy = useRef(false);

  // Check for unsaved changes
  const checkForChanges = useCallback(() => {
    return hasFormDataChanged(formData, initialFormDataRef.current);
  }, [formData]);

  // Update hasUnsavedChanges whenever formData changes
  useEffect(() => {
    setHasUnsavedChanges(checkForChanges());
  }, [formData, checkForChanges]);

  // Set initial form data after loading (for editing) or on mount (for creating)
  useEffect(() => {
    if (!isEditing && !initialFormDataRef.current) {
      initialFormDataRef.current = { ...DEFAULT_FORM_DATA };
    }
  }, [isEditing]);

  // Warn on browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Load prompt data if editing
  useEffect(() => {
    if (isEditing) {
      // Skip load if we just created this prompt (data already in state)
      if (justCreatedRef.current) {
        justCreatedRef.current = false;
        setIsLoading(false);
        return;
      }
      loadPrompt();
    }
  }, [promptId]);

  // Load RAG examples when editing
  useEffect(() => {
    if (isEditing && promptId) {
      loadRagExamples();
    }
  }, [promptId, isEditing]);

  useEffect(() => {
    if (!isEditing && defaultModel) {
      setFormData(previous => previous.model ? previous : { ...previous, model: defaultModel });
      if (initialFormDataRef.current && !initialFormDataRef.current.model) {
        initialFormDataRef.current = { ...initialFormDataRef.current, model: defaultModel };
      }
    }
  }, [defaultModel, isEditing]);

  // Load pre-prompts
  useEffect(() => {
    fetchPrePrompts()
      .then(options => setPrePromptOptions(sortByDisplayLabel(options, prompt => prompt.name)))
      .catch(err => console.error('Error fetching prePrompts:', err));
  }, []);

  // Load reference data types
  useEffect(() => {
    fetchReferenceDataTypes()
      .then(types => setReferenceDataTypes(sortByDisplayLabel(types, type => type.name)))
      .catch(err => console.error('Error fetching reference data types:', err));
  }, []);

  // Load document field types (output schemas)
  useEffect(() => {
    fetchDocumentFieldTypes()
      .then(setDocumentFieldTypes)
      .catch(err => console.error('Error fetching document field types:', err));
  }, []);

  // Load patients
  useEffect(() => {
    fetchPatients(currentUser?.uid)
      .then(patientList => setPatients(sortByDisplayLabel(
        patientList,
        patient => `${patient.lastName || ''}, ${patient.firstName || ''}`
      )))
      .catch(err => console.error('Error fetching patients:', err))
      .finally(() => setIsPatientsLoading(false));
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    fetchCurrentContextTestDefaults(currentUser.uid)
      .then(({ context, documentTypes }) => {
        setCurrentContextDocumentTypes(documentTypes);
        setCurrentContextTest((previous) => ({
          ...context,
          documentName: previous.documentName || documentTypes[0]?.name || '',
        }));
      })
      .catch((err) => console.error('Error loading Current Context test defaults:', err));
  }, [currentUser?.uid]);

  // Timer for testing
  useEffect(() => {
    let interval;
    if (isTesting) {
      setElapsedTime(0);
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 0.1);
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isTesting]);

  const loadPrompt = async () => {
    try {
      const result = await fetchPrompt(promptId);
      if (result) {
        setFormData(result.formData);
        setVersionHistory(result.versionHistory);
        // Set initial form data after loading
        initialFormDataRef.current = { ...result.formData };
      } else {
        setError('Prompt not found');
      }
    } catch (err) {
      console.error('Error loading prompt:', err);
      setError('Failed to load prompt');
    }
    setIsLoading(false);
  };

  // === RAG Examples Handlers ===
  const loadRagExamples = async () => {
    setIsLoadingRagExamples(true);
    try {
      const examples = await fetchRagExamples(promptId);
      setRagExamples(examples);
    } catch (err) {
      console.error('Error loading RAG examples:', err);
    }
    setIsLoadingRagExamples(false);
  };

  const handleExportRagExamples = () => {
    if (ragExamples.length === 0) {
      setError('No RAG examples to export');
      return;
    }

    const jsonl = exportRagExamplesToJsonl(ragExamples, formData.name);
    const blob = new Blob([jsonl], { type: 'application/jsonl' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formData.name.toLowerCase().replace(/\s+/g, '-')}-rag-examples.jsonl`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSuccess('Archived examples exported successfully');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.checked }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Parse historyFields string to array
  const getSelectedFields = () => parseHistoryFields(formData.historyFields);

  // Toggle a field in the historyFields string
  const handleToggleHistoryField = (fieldValue) => {
    const newFields = toggleHistoryField(formData.historyFields, fieldValue);
    handleInputChange('historyFields', newFields);
  };

  // Count total fields for display (from dynamic options)
  const totalFieldCount = historyFieldOptions.reduce((acc, cat) => acc + cat.fields.length, 0);

  // Detect if content looks like HTML
  const isHtmlContent = (content) => {
    if (!content) return false;
    const trimmed = content.trim();
    return trimmed.startsWith('<') || /<[a-z][\s\S]*>/i.test(trimmed);
  };

  // Strip markdown code fences if present (handles ```json ... ``` with or without newlines)
  const stripMarkdownCodeFence = (content) => {
    if (!content) return content;
    const trimmed = content.trim();
    // Match code fences with optional language tag, handles both newlines and spaces
    const match = trimmed.match(/^```(?:json|JSON)?\s*([\s\S]*?)\s*```$/);
    return match ? match[1].trim() : trimmed;
  };

  // Detect if content is a JSON array
  const isJsonArray = (content) => {
    if (!content) return false;
    const cleaned = stripMarkdownCodeFence(content);
    if (!cleaned.startsWith('[')) return false;
    try {
      const parsed = JSON.parse(cleaned);
      return Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object';
    } catch {
      return false;
    }
  };

  // Detect if content is a JSON object
  const isJsonObject = (content) => {
    if (!content) return false;
    const cleaned = stripMarkdownCodeFence(content);
    if (!cleaned.startsWith('{')) return false;
    try {
      const parsed = JSON.parse(cleaned);
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
    } catch {
      return false;
    }
  };

  // Parse JSON safely
  const parseJsonArray = (content) => {
    try {
      const cleaned = stripMarkdownCodeFence(content);
      return JSON.parse(cleaned);
    } catch {
      return [];
    }
  };

  // Parse JSON object safely
  const parseJsonObject = (content) => {
    try {
      const cleaned = stripMarkdownCodeFence(content);
      return JSON.parse(cleaned);
    } catch {
      return {};
    }
  };

  // Format cell value for display
  const formatCellValue = (value) => {
    if (value === null) return <Typography variant="caption" color="text.disabled">null</Typography>;
    if (value === undefined) return <Typography variant="caption" color="text.disabled">undefined</Typography>;
    if (value === true) return <Chip label="true" size="small" color="success" sx={{ height: 20, fontSize: 'var(--text-xs)' }} />;
    if (value === false) return <Chip label="false" size="small" color="default" sx={{ height: 20, fontSize: 'var(--text-xs)' }} />;
    if (typeof value === 'number') return <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>;
    if (Array.isArray(value)) return <Typography variant="body2" sx={{ fontSize: 'var(--text-xs)', fontFamily: 'monospace' }}>{JSON.stringify(value)}</Typography>;
    if (typeof value === 'object') return <Typography variant="body2" sx={{ fontSize: 'var(--text-xs)', fontFamily: 'monospace' }}>{JSON.stringify(value)}</Typography>;
    if (typeof value === 'string' && value.length > 100) return <Typography variant="body2" sx={{ fontSize: 'var(--text-xs)' }}>{value}</Typography>;
    return String(value);
  };

  const getDisplayText = () => testResult || '';

  // Update handleRunTest to auto-detect format
  const handleRunTest = async () => {
    const validation = validateTestRequirements({
      transcript,
      prompt: formData.prompt,
      formData,
      selectedPatient
    });
    
    if (!validation.isValid) {
      setError(validation.error);
      return;
    }

    setIsTesting(true);
    setTestResult('');
    setError('');
    setTestStats(null);
    setElapsedTime(0);

    const startTime = Date.now();

    try {
      // Fetch patient data and history if patient options are enabled
      let patientData = null;
      let patientHistory = null;

      if (selectedPatient && (formData.usePatientName || formData.usePatientData || formData.useFieldHistory || formData.historyOnlyMode)) {
        const patientContext = await fetchPatientWithHistory(selectedPatient, { fullHistory: formData.fullHistory });
        patientData = patientContext.patientData;
        patientHistory = patientContext.patientHistory;

        if (!patientData) {
          throw new Error('Failed to load patient data');
        }
      }

      // Fetch reference data if selected
      let referenceData = null;
      if (formData.referenceData) {
        const selectedType = referenceDataTypes.find(t => t.id === formData.referenceData);
        if (selectedType) {
          const items = await fetchReferenceDataItems(formData.referenceData);
          referenceData = {
            name: selectedType.name,
            promptInstruction: selectedType.promptInstruction,
            items
          };
        }
      }

      // Get document fields schema if selected
      let documentFieldsData = null;
      if (formData.documentFields) {
        const selectedFieldType = documentFieldTypes.find(t => t.id === formData.documentFields);
        if (selectedFieldType) {
          documentFieldsData = {
            name: selectedFieldType.name,
            fields: selectedFieldType.fields
          };
        }
      }

      // Assemble prompt with all injections (matches production behavior)
      // The assembled prompt WITHOUT transcript is sent to the LLM (transcript sent separately)
      const finalPrompt = assemblePrompt({
        prompt: formData.prompt,
        prePromptId: formData.prePrompt,
        prePromptOptions,
        usePatientData: formData.usePatientData,
        usePatientName: formData.usePatientName,
        useFieldHistory: formData.useFieldHistory,
        useCurrentContext: formData.useCurrentContext,
        historyOnlyMode: formData.historyOnlyMode,
        compactHistory: formData.compactHistory,
        historyFields: formData.historyFields,
        patientData,
        patientHistory,
        referenceData,
        documentFieldsData,
        currentContext: {
          ...currentContextTest,
          sessionDuration: currentContextUploadMode ? '' : currentContextTest.sessionDuration,
        },
        historyFieldOptions
      });

      // Display version includes transcript for visibility (transcript sent separately to LLM)
      const displayPrompt = assemblePrompt({
        prompt: formData.prompt,
        prePromptId: formData.prePrompt,
        prePromptOptions,
        usePatientData: formData.usePatientData,
        usePatientName: formData.usePatientName,
        useFieldHistory: formData.useFieldHistory,
        useCurrentContext: formData.useCurrentContext,
        historyOnlyMode: formData.historyOnlyMode,
        compactHistory: formData.compactHistory,
        historyFields: formData.historyFields,
        patientData,
        patientHistory,
        referenceData,
        documentFieldsData,
        currentContext: {
          ...currentContextTest,
          sessionDuration: currentContextUploadMode ? '' : currentContextTest.sessionDuration,
        },
        historyFieldOptions,
        transcript
      });
      setAssembledPrompt(displayPrompt);

      const { result } = await testPrompt(finalPrompt, transcript, formData.model);
      const elapsed = ((Date.now() - startTime) / 1000).toFixed(2);
      
      // Strip markdown code fences if present
      const cleanedResult = stripMarkdownCodeFence(result);
      setTestResult(cleanedResult);
      
      // Auto-switch view based on content type
      if (isJsonArray(cleanedResult) || isJsonObject(cleanedResult)) {
        setOutputView('table');
      } else if (isHtmlContent(cleanedResult)) {
        setOutputView('rendered');
      } else {
        setOutputView('raw');
      }
      
      setTestStats(calculateTestStats({
        promptLength: finalPrompt.length,
        transcriptLength: transcript.length,
        outputLength: cleanedResult.length,
        responseTime: elapsed,
        patientData,
        assembledPrompt: finalPrompt
      }));
    } catch (err) {
      setTestResult(`Error: ${err.message}`);
      setOutputView('raw');
      setTestStats(null);
    } finally {
      setIsTesting(false);
    }
  };

  const handleCopyResult = () => {
    navigator.clipboard.writeText(testResult);
    setSuccess('Result copied to clipboard');
    setTimeout(() => setSuccess(''), 2000);
  };

  const handleLoadSampleTranscript = async (filename) => {
    setIsLoadingTranscript(true);
    setError('');
    try {
      const text = await loadSampleTranscript(filename);
      setTranscript(text);
    } catch (err) {
      setError('Failed to load sample transcript');
    }
    setIsLoadingTranscript(false);
  };

  const handleSave = async () => {
    if (!canSelectGeminiModel(formData.model, models, modelPolicyEnforced)) {
      setError('Select an available AI model before saving.');
      return;
    }
    const validation = validateFormData(formData);
    if (!validation.isValid) {
      setError(validation.error);
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const { promptData, newVersion } = await savePrompt(formData, promptId, isEditing, versionHistory, testStats, currentUser);
      setSuccess(isEditing ? 'Prompt saved' : 'Prompt created');
      setVersionHistory(promptData.versions);
      setFormData(prev => ({ ...prev, version: newVersion }));
      
      // Reset initial form data after successful save
      initialFormDataRef.current = { ...formData, version: newVersion };
      setHasUnsavedChanges(false);

      // If creating new prompt, navigate to edit URL so subsequent saves work
      if (!isEditing && promptData.id) {
        justCreatedRef.current = true;
        
        // Store prompt data and show modal to create step/section
        setCreatedPromptData({
          id: promptData.id,
          name: formData.name,
          type: formData.type,
          model: formData.model
        });
        linkedCreationAttempt.current = null;
        setLinkedCreationError('');
        setCreateStepSectionModalOpen(true);
        
        navigate(`/admin/prompts/edit/${promptData.id}`, { replace: true });
      }
    } catch (err) {
      console.error('Error saving prompt:', err);
      setError('Failed to save prompt: ' + err.message);
    }

    setIsSaving(false);
  };

  // The prompt is already saved; commit only the optional linked group.
  const handleCreateStepSection = async () => {
    if (linkedCreationBusy.current || !createdPromptData || (!createStep && !createSection)) return;
    linkedCreationBusy.current = true;
    setIsCreatingStepSection(true);
    setLinkedCreationError('');
    try {
      linkedCreationAttempt.current ||= newLinkedCreationAttempt(db);
      await createLinkedConfiguration(db, linkedCreationAttempt.current, {
        name: createdPromptData.name,
        userId: currentUser?.uid || currentUser?.email || 'admin',
        promptId: createdPromptData.id,
        processingType: createStep ? 'submitToVertexAI' : 'none',
        sectionType: createSection ? 'EditableTextSection' : 'none',
        createDataField: true,
        fieldCategory: 'From Prompt',
      });
      const created = [];
      if (createStep) created.push('Step');
      if (createSection) created.push('Section');
      setSuccess(`Created: ${created.join(" and ")}`);
      setCreateStepSectionModalOpen(false);
      setCreatedPromptData(null);
      linkedCreationAttempt.current = null;
    } catch (err) {
      console.error('Error creating step/section:', err);
      setLinkedCreationError('Failed to create step/section: ' + err.message);
    } finally {
      linkedCreationBusy.current = false;
      setIsCreatingStepSection(false);
    }
  };

  const handlePublishClick = () => {
    if (!canSelectGeminiModel(formData.model, models, modelPolicyEnforced)) {
      setError('Select and save an available AI model before publishing.');
      return;
    }
    if (hasUnsavedChanges) {
      setError('Please save your changes before publishing');
      return;
    }
    setConfirmDialog({ open: true, type: 'publish' });
  };

  const handlePublishConfirm = async () => {
    setConfirmDialog({ open: false, type: null });
    setIsPublishing(true);
    setError('');

    try {
      await publishPrompt(promptId, formData.version);
      setFormData(prev => ({ ...prev, publishedVersion: formData.version }));
      initialFormDataRef.current = { ...initialFormDataRef.current, publishedVersion: formData.version };
      setSuccess(`Version ${formData.version} published`);
    } catch (err) {
      console.error('Error publishing prompt:', err);
      setError('Failed to publish prompt');
    }

    setIsPublishing(false);
  };

  const handleDeleteClick = () => {
    if (!isEditing) return;
    setConfirmDialog({ open: true, type: 'delete' });
  };

  const handleDeleteConfirm = async () => {
    setConfirmDialog({ open: false, type: null });
    setIsSaving(true);
    setError('');

    try {
      await deletePrompt(promptId);
      setSuccess('Prompt deleted successfully');
      setTimeout(() => navigate('/admin/prompts'), 1500);
    } catch (err) {
      console.error('Error deleting prompt:', err);
      setError('Failed to delete prompt');
    }

    setIsSaving(false);
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm(
        "You have unsaved changes. Are you sure you want to leave this page?"
      );
      if (!confirmLeave) return;
    }
    navigate('/admin/prompts');
  };

  const handleRestoreVersion = (version) => {
    if (!window.confirm(`Are you sure you want to restore to version ${version.version}? This will create a new version.`)) {
      return;
    }
    const restoredData = parseVersionToFormData(version, formData);
    setFormData(restoredData);
    setSuccess(`Restored to version ${version.version}. Click Save to create a new version with this content.`);
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="header-container">
          <Tooltip title="Go back" arrow>
            <button className="back-button" onClick={handleBack}>&larr;</button>
          </Tooltip>
          <div className="page-title">Loading...</div>
        </div>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  const selectedFields = getSelectedFields();

  // Determine publish state
  const isCurrentVersionPublished = formData.publishedVersion === formData.version;
  const hasPublishedVersion = formData.publishedVersion !== null;

  // Handle admin tab navigation (matches Admin.js pattern)
  const handleAdminTabChange = (event, newValue) => {
    // Clicking the current tab (Prompts) goes back to prompts list
    if (newValue === 4) {
      if (hasUnsavedChanges) {
        const confirmLeave = window.confirm(
          "You have unsaved changes. Are you sure you want to leave this page?"
        );
        if (!confirmLeave) return;
      }
      navigate('/admin/prompts');
      return;
    }
    
    // Navigating to other admin tabs
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm(
        "You have unsaved changes. Are you sure you want to leave this page?"
      );
      if (!confirmLeave) return;
    }
    navigate(adminTabPath(newValue));
  };

  return (
    <div className="page-container">
      {/* Header: Admin title + main tabs (matches Admin.js) */}
      <div className="header-container" style={{ justifyContent: 'space-between' }}>
        <div className="page-title" style={{ marginBottom: 0 }}>Admin</div>
        <Tabs 
          value={4}
          onChange={handleAdminTabChange} 
          aria-label="admin tabs"
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: 'auto' }}
        >
          {ADMIN_TABS.map(tab => <Tab key={tab.key} label={tab.label} sx={{ minHeight: 'auto', py: 1 }} />)}
        </Tabs>
      </div>

      {/* Toolbar: back button, title, status chips, action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, px: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Back to Prompts" arrow>
            <button className="back-button" onClick={handleBack}>&larr;</button>
          </Tooltip>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500 }}>
            {isEditing ? 'Edit Prompt' : 'New Prompt'}
          </Typography>
          {isEditing && formData.name && (
            <Typography variant="h6" component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
              — {formData.name}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEditing && (
            <>
              <Chip 
                label={`v${formData.version}`} 
                color="primary" 
                size="small" 
              />
              {hasPublishedVersion && (
                <Chip 
                  label={isCurrentVersionPublished ? 'Published' : `v${formData.publishedVersion} published`}
                  color={isCurrentVersionPublished ? 'success' : 'default'}
                  size="small" 
                  variant={isCurrentVersionPublished ? 'filled' : 'outlined'}
                />
              )}
              {!hasPublishedVersion && (
                <Chip label="Draft" color="warning" size="small" variant="outlined" />
              )}
            </>
          )}
          {hasUnsavedChanges && (
            <Chip label="Unsaved changes" color="warning" size="small" />
          )}
          {isEditing && (
            <Button
              startIcon={<Trash2 size={16} />}
              onClick={handleDeleteClick}
              disabled={isSaving || isPublishing}
              color="error"
              variant="outlined"
              size="small"
            >
              Delete
            </Button>
          )}
          <Button
            startIcon={<X size={16} />}
            onClick={handleBack}
            disabled={isSaving || isPublishing}
            variant="outlined"
            size="small"
          >
            Cancel
          </Button>
          <Tooltip title="Help" arrow>
            <IconButton onClick={() => setHelpDialogOpen(true)} size="small">
              <HelpCircle size={18} />
            </IconButton>
          </Tooltip>
          <Button
            startIcon={<Save size={16} />}
            onClick={handleSave}
            disabled={isSaving || isPublishing || !hasUnsavedChanges}
            variant="contained"
            color="primary"
            size="small"
          >
            {isSaving ? 'Saving...' : (hasUnsavedChanges ? 'Save' : 'Saved')}
          </Button>
          {isEditing && (
            <Button
              startIcon={<Upload size={16} />}
              onClick={handlePublishClick}
              disabled={isSaving || isPublishing || hasUnsavedChanges || isCurrentVersionPublished}
              variant={isCurrentVersionPublished ? 'outlined' : 'contained'}
              color="success"
              size="small"
            >
              {isPublishing ? 'Publishing...' : (isCurrentVersionPublished ? 'Published' : 'Publish')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Help Dialog */}
      <ModalPortal isOpen={helpDialogOpen}>
        <Dialog 
          open={helpDialogOpen} 
          onClose={() => setHelpDialogOpen(false)} 
          maxWidth="sm" 
          fullWidth
          sx={{ zIndex: 'var(--z-modal-backdrop)' }}
        >
          <DialogTitle>Prompt Builder Help</DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Prompt Settings</Typography>
            <Typography variant="body2" component="div" paragraph>
              <strong>Pre-Prompt</strong> — Shared instructions prepended to this prompt. Useful for standard formatting rules.<br /><br />
              <strong>Model</strong> — Flash models are faster and cheaper; Pro models handle complex tasks better.<br /><br />
              <strong>Active</strong> — Only active prompts are available for document processing.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Patient Context Options</Typography>
            <Typography variant="body2" component="div" paragraph>
              <strong>Include Patient Name</strong> — Adds the patient's name for personalization.<br /><br />
              <strong>Include Patient Data</strong> — Includes demographics and stored patient info.<br /><br />
              <strong>Include Field History</strong> — Pulls values from previous documents (e.g., past medications, diagnoses).
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Field History</Typography>
            <Typography variant="body2" paragraph>
              When enabled, select which fields to include. The AI receives the most recent values for each selected field from the patient's history.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Testing</Typography>
            <Typography variant="body2" component="div" paragraph>
              1. Select a patient for context (required when using patient options)<br />
              2. Enter or load a transcript (optional when using Field History with fields selected)<br />
              3. Click "Run Test" and review output<br />
              4. Use Raw, Rendered, or Table views to inspect results
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Versions</Typography>
            <Typography variant="body2" paragraph>
              Each save creates a new version. Restore past versions from the Versions tab — restoring creates a new version with the old content.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Publishing</Typography>
            <Typography variant="body2" paragraph>
              <strong>Draft</strong> — New prompts start as drafts. Save and test before publishing.<br />
              <strong>Publish</strong> — Makes the current version live for document processing.<br />
              <strong>Safe editing</strong> — You can save new versions without affecting production until you publish.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Archived Examples</Typography>
            <Typography variant="body2" paragraph>
              RAG grounding is retired. Stored examples remain available for read-only reference and export.
            </Typography>

          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHelpDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </ModalPortal>

      {/* Confirmation Dialog */}
      <ModalPortal isOpen={confirmDialog.open}>
        <Dialog
          open={confirmDialog.open}
          onClose={() => {
            setConfirmDialog({ open: false, type: null });
            setDeleteConfirmText('');
          }}
          maxWidth="xs"
          fullWidth
          sx={{ zIndex: 'var(--z-modal-backdrop)' }}
        >
          <DialogTitle>
            {confirmDialog.type === 'publish' ? 'Publish Prompt' : 'Delete Prompt'}
          </DialogTitle>
          <DialogContent>
            {confirmDialog.type === 'publish' ? (
              <Typography variant="body1">
                Publish version {formData.version}? This will make it the live version used in document processing.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body1">
                  This action cannot be undone. To confirm, type <strong>{formData.name || 'the prompt name'}</strong> below:
                </Typography>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  placeholder="Type prompt name to confirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  error={deleteConfirmText.length > 0 && deleteConfirmText !== formData.name}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setConfirmDialog({ open: false, type: null });
              setDeleteConfirmText('');
            }}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (confirmDialog.type === 'publish') {
                  handlePublishConfirm();
                } else {
                  handleDeleteConfirm();
                  setDeleteConfirmText('');
                }
              }}
              color={confirmDialog.type === 'publish' ? 'success' : 'error'}
              variant="contained"
              disabled={confirmDialog.type === 'delete' && deleteConfirmText !== formData.name}
            >
              {confirmDialog.type === 'publish' ? 'Publish' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </ModalPortal>

      {modelError && <Alert severity="error" sx={{ mt: 3 }}>{modelError}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3, mt: 3 }}>{success}</Alert>}

      <Paper sx={{ p: 0, mt: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Prompt" />
          <Tab label="Notes" />
          <Tab label="Versions" />
          <Tab label="Archived Examples" disabled={!isEditing} />
          <Tab label="Pre-Prompts" />
        </Tabs>

        {activeTab === 0 && (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Row 1: Core Settings */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Name *"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter prompt name"
                variant="outlined"
                size="small"
                sx={{ flex: 2 }}
              />
              
              <FormControl variant="outlined" size="small" sx={{ flex: 1, minWidth: 120 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  label="Type"
                >
                  <MenuItem value="document">Document</MenuItem>
                  <MenuItem value="history">History</MenuItem>
                  <MenuItem value="life-story">Life Story</MenuItem>
                  <MenuItem value="system">System</MenuItem>
                  <MenuItem value="table">Table</MenuItem>
                </Select>
              </FormControl>

              <FormControl variant="outlined" size="small" sx={{ flex: 1.5, minWidth: 180 }}>
                <InputLabel>Model</InputLabel>
                <Select
                  value={formData.model}
                  onChange={(e) => handleInputChange('model', e.target.value)}
                  label="Model"
                  renderValue={value => models.find(model => model.value === value)?.label || `${value} (saved model)`}
                >
                  {formData.model && !models.some(model => model.value === formData.model) && (
                    <MenuItem value={formData.model} disabled sx={{ display: 'none' }}>
                      {formData.model} ({modelPolicyEnforced ? 'unavailable' : 'saved model'})
                    </MenuItem>
                  )}
                  <ListSubheader>Models</ListSubheader>
                  {models.map(model => (
                    <MenuItem key={model.value} value={model.value}>
                      <GeminiModelLabel model={model} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

            </Box>

            {/* Row 2: Pre-Prompt, Patient, and Reference Data */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <SearchableAdminSelect
                  label="Pre-Prompt"
                  value={formData.prePrompt}
                  onChange={selectedId => handleInputChange('prePrompt', selectedId)}
                  options={[
                    { value: '', label: 'None' },
                    ...prePromptOptions.map(prompt => ({ value: prompt.id, label: prompt.name || prompt.id })),
                  ]}
                />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <SearchableAdminSelect
                  label="Patient"
                  value={selectedPatient}
                  onChange={setSelectedPatient}
                  disabled={isPatientsLoading}
                  options={[
                    { value: '', label: isPatientsLoading ? 'Loading...' : 'Select patient...' },
                    ...patients.map(patient => ({
                      value: patient.id,
                      label: `${patient.lastName || ''}, ${patient.firstName || ''}`,
                    })),
                  ]}
                />
              </Box>

              <Tooltip title={formData.type !== "table" ? "Only available for Table type prompts" : "Append reference data (e.g., medications list) with instructions to the prompt"} arrow placement="top">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <SearchableAdminSelect
                    label="Reference Data"
                    value={formData.type === "table" ? formData.referenceData : ""}
                    onChange={selectedId => handleInputChange('referenceData', selectedId)}
                    disabled={formData.type !== "table"}
                    options={[
                      { value: '', label: 'None' },
                      ...referenceDataTypes.map(type => ({
                        value: type.id,
                        label: `${type.name} (${type.itemCount || 0})`,
                      })),
                    ]}
                  />
                </Box>
              </Tooltip>

              <Tooltip title={formData.type !== "table" ? "Only available for Table type prompts" : "Define expected output JSON schema (e.g., Medications). Appends format instructions to the prompt."} arrow placement="top">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <SearchableAdminSelect
                    label="Output Schema"
                    value={formData.type === "table" ? formData.documentFields : ""}
                    onChange={selectedId => handleInputChange('documentFields', selectedId)}
                    disabled={formData.type !== "table"}
                    options={[
                      { value: '', label: 'None' },
                      ...documentFieldTypes.map(type => ({
                        value: type.id,
                        label: `${type.displayName} (${type.fieldCount || 0} fields)`,
                      })),
                    ]}
                  />
                </Box>
              </Tooltip>
            </Box>

            {/* Row 3: Data Options */}
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
              <Tooltip 
                title="Include patient demographics (age, pronouns). Patient will be referred to as 'the patient' - name will NOT be used." 
                arrow
              >
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.usePatientData} 
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Selecting usePatientData deselects usePatientName
                          setFormData(prev => ({ 
                            ...prev, 
                            usePatientData: true, 
                            usePatientName: false 
                          }));
                        } else {
                          handleCheckboxChange('usePatientData')(e);
                        }
                      }} 
                      size="small" 
                    />
                  }
                  label={<Typography variant="body2">Use Patient Data</Typography>}
                />
              </Tooltip>
              <Tooltip 
                title="Include patient demographics (age, pronouns) AND use their full name formally in the output." 
                arrow
              >
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.usePatientName} 
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Selecting usePatientName deselects usePatientData
                          setFormData(prev => ({ 
                            ...prev, 
                            usePatientName: true, 
                            usePatientData: false 
                          }));
                        } else {
                          handleCheckboxChange('usePatientName')(e);
                        }
                      }} 
                      size="small" 
                    />
                  }
                  label={<Typography variant="body2">Use Patient Name</Typography>}
                />
              </Tooltip>
              <Tooltip 
                title="Include selected fields from patient's previous documents. Configure fields below when enabled." 
                arrow
              >
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.useFieldHistory} 
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Selecting useFieldHistory deselects historyOnlyMode (mutually exclusive)
                          setFormData(prev => ({ 
                            ...prev, 
                            useFieldHistory: true, 
                            historyOnlyMode: false 
                          }));
                        } else {
                          handleCheckboxChange('useFieldHistory')(e);
                        }
                      }}
                      size="small" 
                    />
                  }
                  label={<Typography variant="body2">Use Field History</Typography>}
                />
              </Tooltip>
              <Tooltip 
                title="Used for History Summary steps on the Life Story. Choose one field below to Summarize." 
                arrow
              >
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.historyOnlyMode} 
                      onChange={(e) => {
                        if (e.target.checked) {
                          // Selecting historyOnlyMode deselects useFieldHistory (mutually exclusive)
                          // Also clear historyFields since we need single-select
                          setFormData(prev => ({ 
                            ...prev, 
                            historyOnlyMode: true, 
                            useFieldHistory: false,
                            historyFields: ''
                          }));
                        } else {
                          handleCheckboxChange('historyOnlyMode')(e);
                        }
                      }}
                      size="small" 
                    />
                  }
                  label={<Typography variant="body2">History Only</Typography>}
                />
              </Tooltip>
              <Tooltip 
                title="Strip empty fields, null values, and blank entries from history data before sending. Reduces token usage and noise." 
                arrow
              >
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.compactHistory} 
                      onChange={handleCheckboxChange('compactHistory')}
                      size="small" 
                    />
                  }
                  label={<Typography variant="body2">Compact Format</Typography>}
                />
              </Tooltip>
              <Tooltip 
                title="Send all available patient documents instead of the default limit. Warning: may significantly increase token usage." 
                arrow
              >
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.fullHistory} 
                      onChange={handleCheckboxChange('fullHistory')}
                      size="small" 
                    />
                  }
                  label={<Typography variant="body2">Full History</Typography>}
                />
              </Tooltip>
              <Tooltip
                title="Include the initiating provider, Document Type, recording duration, and the current date/time in the model prompt."
                arrow
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.useCurrentContext}
                      onChange={handleCheckboxChange('useCurrentContext')}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Current Context</Typography>}
                />
              </Tooltip>
            </Box>

            {formData.useCurrentContext && (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle2">Current Context test values</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Test-only values. Production resolves provider and document values on the server.
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setCurrentContextTest((previous) => ({
                      ...previous,
                      currentDateTime: formatCurrentContextDateTime(new Date(), previous.timeZone),
                    }))}
                  >
                    Use current time
                  </Button>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                  <TextField
                    label="Provider name"
                    size="small"
                    value={currentContextTest.providerName}
                    onChange={(event) => setCurrentContextTest((previous) => ({ ...previous, providerName: event.target.value }))}
                  />
                  <TextField
                    label="Provider credentials"
                    size="small"
                    value={currentContextTest.providerCredentials}
                    onChange={(event) => setCurrentContextTest((previous) => ({ ...previous, providerCredentials: event.target.value }))}
                  />
                  <Box>
                    <SearchableAdminSelect
                      label="Document Type name"
                      value={currentContextTest.documentName}
                      onChange={name => setCurrentContextTest(previous => ({ ...previous, documentName: name }))}
                      options={[
                        { value: '', label: 'Empty' },
                        ...[...new Set(currentContextDocumentTypes.map(documentType => documentType.name).filter(Boolean))]
                          .map(name => ({ value: name, label: name })),
                      ]}
                    />
                  </Box>
                  <TextField
                    label="Session duration"
                    size="small"
                    value={currentContextUploadMode ? '' : currentContextTest.sessionDuration}
                    disabled={currentContextUploadMode}
                    placeholder="HH:MM:SS"
                    onChange={(event) => setCurrentContextTest((previous) => ({ ...previous, sessionDuration: event.target.value }))}
                  />
                  <TextField
                    label="Current date/time"
                    size="small"
                    value={currentContextTest.currentDateTime}
                    onChange={(event) => setCurrentContextTest((previous) => ({ ...previous, currentDateTime: event.target.value }))}
                    helperText="ISO 8601 with UTC offset"
                  />
                  <TextField
                    label="Time zone"
                    size="small"
                    value={currentContextTest.timeZone}
                    onChange={(event) => setCurrentContextTest((previous) => ({ ...previous, timeZone: event.target.value }))}
                    helperText="IANA name, for example America/Los_Angeles"
                  />
                </Box>
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={currentContextUploadMode}
                      onChange={(event) => setCurrentContextUploadMode(event.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Upload test (session duration is empty)</Typography>}
                />
              </Box>
            )}

            {/* History Fields Selector - shows for useFieldHistory (multi-select) or historyOnlyMode (single-select) */}
            {(formData.useFieldHistory || formData.historyOnlyMode) && (
              <Box
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.default',
                }}
              >
                {/* Collapsible Header */}
                <Box
                  onClick={() => setIsFieldSelectorCollapsed(!isFieldSelectorCollapsed)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderRadius: isFieldSelectorCollapsed ? 1 : 0,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isFieldSelectorCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formData.historyOnlyMode ? 'History Field (select one)' : 'History Fields'}
                    </Typography>
                    <Chip
                      label={formData.historyOnlyMode 
                        ? (selectedFields.length > 0 
                          ? (historyFieldOptions.flatMap(c => c.fields).find(f => f.value === selectedFields[0])?.label || selectedFields[0])
                          : 'none selected')
                        : `${selectedFields.length} of ${totalFieldCount}`
                      }
                      size="small"
                      color={selectedFields.length > 0 ? 'primary' : 'default'}
                      sx={{ fontSize: 'var(--text-xs)', height: 20 }}
                    />
                    {selectedFields.length > 0 && (
                      <Chip
                        label="Clear All"
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent collapse toggle
                          handleInputChange('historyFields', '');
                        }}
                        onDelete={(e) => {
                          e.stopPropagation();
                          handleInputChange('historyFields', '');
                        }}
                        sx={{ 
                          fontSize: 'var(--text-2xs)', 
                          height: 20,
                          '& .MuiChip-deleteIcon': { fontSize: 'var(--text-sm)' }
                        }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {isFieldSelectorCollapsed ? 'Click to expand' : 'Click to collapse'}
                  </Typography>
                </Box>

                {/* Expandable Content */}
                {!isFieldSelectorCollapsed && (
                  <Box sx={{ px: 2, pb: 2 }}>
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
                      Options update automatically from Data Fields. A section needs a data field with Show in History Fields enabled.{' '}
                      <Link href={dataTabPath(2)} target="_blank" rel="noopener noreferrer">Manage Data Fields (opens in a new tab)</Link>
                      {hiddenHistoryFieldCount > 0 && ` ${hiddenHistoryFieldCount} hidden by that setting.`}
                    </Typography>
                    {historyFieldsLoading && <Typography role="status" variant="body2">Loading history fields…</Typography>}
                    {historyFieldsError && (
                      <Alert severity="warning" sx={{ mb: 1 }} action={<Button onClick={retryHistoryFields}>Retry</Button>}>
                        History fields could not be updated. The options below may be out of date.
                      </Alert>
                    )}
                    {!historyFieldsLoading && !historyFieldsError && totalFieldCount === 0 && (
                      <Typography variant="body2">No history fields are visible. Check Show in History Fields in Data Fields.</Typography>
                    )}
                    <Box
                      sx={{
                        maxHeight: 280,
                        overflowY: 'auto',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 2,
                      }}
                    >
                      {historyFieldOptions.map((category) => (
                        <Box key={category.category}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: 'text.secondary',
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                              fontSize: 'var(--text-2xs)',
                              display: 'block',
                              mb: 0.5,
                            }}
                          >
                            {category.category}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {category.fields.map((field) => {
                              const isSelected = selectedFields.includes(field.value);
                              return (
                                <Chip
                                  key={field.value}
                                  label={field.label}
                                  size="small"
                                  variant={isSelected ? 'filled' : 'outlined'}
                                  color={isSelected ? 'primary' : 'default'}
                                  onClick={() => {
                                    if (formData.historyOnlyMode) {
                                      // Single-select mode: replace selection
                                      if (isSelected) {
                                        // Deselect (clear)
                                        handleInputChange('historyFields', '');
                                      } else {
                                        // Select this one only
                                        handleInputChange('historyFields', field.value);
                                      }
                                    } else {
                                      // Multi-select mode: toggle
                                      handleToggleHistoryField(field.value);
                                    }
                                  }}
                                  sx={{
                                    fontSize: 'var(--text-xs)',
                                    height: 24,
                                    cursor: 'pointer',
                                    '&:hover': {
                                      bgcolor: isSelected ? 'primary.dark' : 'action.hover',
                                    },
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            <TextField
              label="Prompt *"
              value={formData.prompt}
              onChange={(e) => handleInputChange('prompt', e.target.value)}
              fullWidth
              multiline
              rows={16}
              placeholder="Enter the main prompt content here..."
              variant="outlined"
              sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 'var(--text-sm)' } }}
            />

            {/* Transcript Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Load sample:</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={isLoadingTranscript ? <CircularProgress size={14} /> : <FileText size={14} />}
                  onClick={() => handleLoadSampleTranscript('transcript1.txt')}
                  disabled={isLoadingTranscript || formData.historyOnlyMode}
                >
                  Transcript 1
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={isLoadingTranscript ? <CircularProgress size={14} /> : <FileText size={14} />}
                  onClick={() => handleLoadSampleTranscript('transcript2.txt')}
                  disabled={isLoadingTranscript || formData.historyOnlyMode}
                >
                  Transcript 2
                </Button>
              </Box>
              <TextField
                label={
                  formData.historyOnlyMode 
                    ? "Transcript (disabled - History Only mode)" 
                    : formData.useFieldHistory && getSelectedFields().length > 0 
                      ? "Transcript (optional with Field History)" 
                      : "Transcript"
                }
                value={formData.historyOnlyMode ? '' : transcript}
                onChange={(e) => setTranscript(e.target.value)}
                placeholder={
                  formData.historyOnlyMode
                    ? "Not used in History Only mode"
                    : formData.useFieldHistory && getSelectedFields().length > 0 
                      ? "Optional when using Field History - leave empty to test with history data only"
                      : "Paste transcript here or load a sample above..."
                }
                variant="outlined"
                multiline
                rows={2}
                fullWidth
                disabled={formData.historyOnlyMode}
                sx={{ 
                  '& .MuiInputBase-input': { fontSize: 'var(--text-xs)' },
                  ...(formData.historyOnlyMode && { 
                    '& .MuiInputBase-root': { bgcolor: 'action.disabledBackground' }
                  })
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {error && (
                <Alert severity="error" sx={{ flex: 1, py: 0 }}>
                  {error}
                </Alert>
              )}
              <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
                {!error && testStats && !isTesting && (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: testStats.promptLength >= 20000 ? 'error.main' : 'text.secondary' 
                      }}
                    >
                      Prompt: <strong>{testStats.promptLength.toLocaleString()}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Transcript: <strong>{testStats.transcriptLength.toLocaleString()}</strong>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Output: <strong>{testStats.outputLength.toLocaleString()}</strong>
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: parseFloat(testStats.responseTime) < 20 
                          ? 'success.main' 
                          : parseFloat(testStats.responseTime) <= 30 
                            ? 'warning.main' 
                            : 'error.main'
                      }}
                    >
                      Time: <strong>{testStats.responseTime}s</strong>
                    </Typography>
                    {testStats.hasPatientData && (
                      <Typography variant="caption" color="primary">
                        Patient: <strong>✓</strong>
                      </Typography>
                    )}
                    {testStats.historyDocCount > 0 && (
                      <Typography variant="caption" color="primary">
                        History: <strong>{testStats.historyDocCount} docs</strong>
                      </Typography>
                    )}
                  </Box>
                )}
                {!error && isTesting && (
                  <Typography variant="caption" color="text.secondary">
                    Running: <strong>{elapsedTime.toFixed(1)}s</strong>
                  </Typography>
                )}
                <Button
                  startIcon={isTesting ? <CircularProgress size={16} color="inherit" /> : <Play size={16} />}
                  onClick={handleRunTest}
                  disabled={isTesting || !formData.prompt.trim()}
                  variant="contained"
                  color="secondary"
                >
                  {isTesting ? 'Running...' : 'Test Prompt'}
                </Button>
              </Box>
            </Box>

            {/* Output Section */}
            <Box sx={{ position: 'relative' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Output</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ToggleButtonGroup
                    value={outputView}
                    exclusive
                    onChange={(e, val) => val && setOutputView(val)}
                    size="small"
                  >
                    <ToggleButton value="raw" sx={{ py: 0.5, px: 1.5 }}>
                      <Code size={14} style={{ marginRight: 4 }} />
                      Raw
                    </ToggleButton>
                    <ToggleButton value="rendered" sx={{ py: 0.5, px: 1.5 }}>
                      <Eye size={14} style={{ marginRight: 4 }} />
                      Rendered
                    </ToggleButton>
                    <ToggleButton value="table" sx={{ py: 0.5, px: 1.5 }}>
                      <Table size={14} style={{ marginRight: 4 }} />
                      Table
                    </ToggleButton>
                    <ToggleButton value="prompt" sx={{ py: 0.5, px: 1.5 }}>
                      <FileText size={14} style={{ marginRight: 4 }} />
                      Prompt
                    </ToggleButton>
                  </ToggleButtonGroup>
                  {testResult && (
                    <Tooltip title="Copy result" arrow>
                      <IconButton onClick={handleCopyResult} size="small">
                        <Copy size={16} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              {outputView === 'raw' && (
                <TextField
                  value={getDisplayText() || 'Output will appear here...'}
                  fullWidth
                  multiline
                  rows={12}
                  variant="outlined"
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: 'var(--text-sm)',
                      color: testResult ? 'text.primary' : 'text.secondary'
                    }
                  }}
                />
              )}

              {outputView === 'rendered' && formData.type === 'document' && (
                <ReadOnlyTextRenderer
                  content={testResult ? getDisplayText() : ''}
                  placeholder="Output will appear here..."
                />
              )}

              {outputView === 'rendered' && formData.type !== 'document' && (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    minHeight: 288,
                    maxHeight: 400,
                    overflowY: 'auto',
                    bgcolor: 'background.paper',
                    '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 2, mb: 1 },
                    '& p': { mb: 1 },
                    '& ul, & ol': { pl: 3, mb: 1 },
                    '& table': { borderCollapse: 'collapse', width: '100%', mb: 2 },
                    '& th, & td': { border: 1, borderColor: 'divider', p: 1, textAlign: 'left' },
                    '& th': { bgcolor: 'action.hover', fontWeight: 'bold' },
                  }}
                >
                  {testResult ? (
                    <SafeHtml html={getDisplayText()} />
                  ) : (
                    <Typography color="text.secondary">Output will appear here...</Typography>
                  )}
                </Box>
              )}

              {outputView === 'table' && (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    minHeight: 288,
                    maxHeight: 400,
                    overflowY: 'auto',
                    bgcolor: 'background.paper',
                  }}
                >
                  {testResult && isJsonArray(testResult) ? (
                    (() => {
                      const data = parseJsonArray(testResult);
                      const columns = Object.keys(data[0] || {});
                      return (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
                          <thead>
                            <tr>
                              {columns.map((col) => (
                                <th
                                  key={col}
                                  style={{
                                    padding: '8px 12px',
                                    textAlign: 'left',
                                    borderBottom: '2px solid #e0e0e0',
                                    backgroundColor: 'var(--color-neutral-light)',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {col.replace(/_/g, ' ')}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {data.map((row, idx) => (
                              <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                {columns.map((col) => (
                                  <td
                                    key={col}
                                    style={{
                                      padding: '8px 12px',
                                      borderBottom: '1px solid #e0e0e0',
                                      verticalAlign: 'top',
                                    }}
                                  >
                                    {formatCellValue(row[col])}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()
                  ) : testResult && isJsonObject(testResult) ? (
                    (() => {
                      const data = parseJsonObject(testResult);
                      const keys = Object.keys(data);
                      return (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' }}>
                          <thead>
                            <tr>
                              <th
                                style={{
                                  padding: '8px 12px',
                                  textAlign: 'left',
                                  borderBottom: '2px solid #e0e0e0',
                                  backgroundColor: 'var(--color-neutral-light)',
                                  fontWeight: 600,
                                  width: '200px',
                                }}
                              >
                                Field
                              </th>
                              <th
                                style={{
                                  padding: '8px 12px',
                                  textAlign: 'left',
                                  borderBottom: '2px solid #e0e0e0',
                                  backgroundColor: 'var(--color-neutral-light)',
                                  fontWeight: 600,
                                }}
                              >
                                Value
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {keys.map((key, idx) => (
                              <tr key={key} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                <td
                                  style={{
                                    padding: '8px 12px',
                                    borderBottom: '1px solid #e0e0e0',
                                    verticalAlign: 'top',
                                    fontWeight: 500,
                                    color: 'var(--color-gray)',
                                  }}
                                >
                                  {key.replace(/_/g, ' ')}
                                </td>
                                <td
                                  style={{
                                    padding: '8px 12px',
                                    borderBottom: '1px solid #e0e0e0',
                                    verticalAlign: 'top',
                                  }}
                                >
                                  {formatCellValue(data[key])}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      );
                    })()
                  ) : (
                    <Box sx={{ p: 2 }}>
                      <Typography color="text.secondary">
                        {testResult ? 'Output is not valid JSON. Switch to Raw view.' : 'Output will appear here...'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {outputView === 'prompt' && (
                <TextField
                  value={assembledPrompt || 'Run a test to see the assembled prompt...'}
                  fullWidth
                  multiline
                  rows={12}
                  variant="outlined"
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontFamily: 'monospace',
                      fontSize: 'var(--text-sm)',
                      color: assembledPrompt ? 'text.primary' : 'text.secondary'
                    }
                  }}
                />
              )}

            </Box>
          </Box>
        )}

        {/* Notes Tab */}
        {activeTab === 1 && (
          <Box sx={{ p: 3, height: 'calc(100vh - 400px)', minHeight: '400px' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Notes</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Freeform notes about this prompt. Notes are saved with the prompt but not versioned.
            </Typography>
            <AdminNoteEditor
              value={formData.notes}
              onChange={(value) => handleInputChange('notes', value)}
              placeholder="Add notes about this prompt's purpose, design decisions, edge cases, testing observations..."
              minHeight="300px"
            />
          </Box>
        )}

        {/* Versions Tab */}
        {activeTab === 2 && (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {isEditing && versionHistory.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <History size={20} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Version History ({versionHistory.length} versions)
                  </Typography>
                </Box>
                <List>
                  {[...versionHistory].reverse().map((version, index) => (
                    <ListItem
                      key={index}
                      divider
                      secondaryAction={
                        <Tooltip title="Restore this version" arrow>
                          <IconButton
                            onClick={() => handleRestoreVersion(version)}
                            disabled={version.version === formData.version}
                            size="small"
                          >
                            <RotateCcw size={16} />
                          </IconButton>
                        </Tooltip>
                      }
                    >
                      <ListItemText
                        disableTypography
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="subtitle2">Version {version.version}</Typography>
                            {version.version === formData.version && (
                              <Chip label="Current" size="small" color="primary" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {(() => {
                                if (!version.createdAt) return 'No date';
                                if (version.createdAt?.toDate) return version.createdAt.toDate().toLocaleString();
                                if (version.createdAt?.seconds) return new Date(version.createdAt.seconds * 1000).toLocaleString();
                                if (version.createdAt instanceof Date) return version.createdAt.toLocaleString();
                                const parsed = new Date(version.createdAt);
                                return isNaN(parsed.getTime()) ? 'Unknown date' : parsed.toLocaleString();
                              })()}
                              {version.createdBy && ` • ${version.createdBy}`}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {version.prompt.substring(0, 100)}...
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {isEditing && versionHistory.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No version history available yet. Save changes to create the first version.
              </Typography>
            )}

            {!isEditing && (
              <Typography variant="body2" color="text.secondary">
                Version history will be available after the prompt is created.
              </Typography>
            )}
          </Box>
        )}

        {/* Archived examples remain read/export-only after RAG retirement. */}
        {activeTab === 3 && (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">RAG grounding is retired. Stored examples are preserved for reference and export.</Alert>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">
                  Archived Examples ({ragExamples.length})
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {ragExamples.length > 0 && (
                  <Button
                    startIcon={<Download size={16} />}
                    onClick={handleExportRagExamples}
                    variant="outlined"
                    size="small"
                  >
                    Export
                  </Button>
                )}
              </Box>
            </Box>

            {isLoadingRagExamples ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : ragExamples.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                <Typography>No archived examples.</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '150px' }}>Entry ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: '150px' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Content</TableCell>

                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ragExamples.map((example) => (
                      <TableRow key={example.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {example.entryId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={example.type || 'N/A'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            maxWidth: '500px'
                          }}>
                            {example.content}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}

        {/* Pre-Prompts Tab - Read Only */}
        {activeTab === 4 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Pre-Prompts</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Read-only view of available pre-prompts. These are shared instructions that can be prepended to prompts.
            </Typography>
            {prePromptOptions.length === 0 ? (
              <Typography color="text.secondary">No pre-prompts available.</Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Content</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {prePromptOptions.map((pp) => (
                      <TableRow key={pp.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top', width: '150px' }}>
                          {pp.name}
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              whiteSpace: 'pre-wrap', 
                              fontFamily: 'monospace', 
                              fontSize: 'var(--text-sm)',
                              backgroundColor: 'grey.50',
                              p: 1,
                              borderRadius: 1,
                              maxHeight: '200px',
                              overflow: 'auto'
                            }}
                          >
                            {pp.content || <em style={{ color: 'var(--color-text-muted)' }}>No content</em>}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* Create Step/Section Modal - shown after first prompt save */}
      <ModalPortal isOpen={createStepSectionModalOpen}>
        <Dialog
          open={createStepSectionModalOpen}
          onClose={() => !isCreatingStepSection && setCreateStepSectionModalOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Create Step & Section?</DialogTitle>
          <DialogContent>
            {linkedCreationError && <Alert severity="error" sx={{ mb: 2 }}>{linkedCreationError}</Alert>}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Would you like to create an AI Step and Document Section for this prompt?
              They will be linked to a shared data field.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createStep}
                    onChange={(e) => setCreateStep(e.target.checked)}
                    disabled={isCreatingStepSection}
                  />
                }
                label="Create AI Step"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createSection}
                    onChange={(e) => setCreateSection(e.target.checked)}
                    disabled={isCreatingStepSection}
                  />
                }
                label="Create Document Section"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setCreateStepSectionModalOpen(false)} 
              disabled={isCreatingStepSection}
            >
              Skip
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateStepSection}
              disabled={isCreatingStepSection || (!createStep && !createSection)}
            >
              {isCreatingStepSection ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </ModalPortal>
    </div>
  );
};

export default AdminPromptBuilder;
